/**
 * Sales order detail — `/sales/orders/:id`.
 *
 * Read-only: the contract publishes no sales write. Two things this screen is
 * careful about.
 *
 * Customer identity is not part of the order. The detail projection carries
 * references and commercial facts only, and the customer panel starts closed:
 * revealing it issues the separate purpose-specific read, which the server
 * audits whether it succeeds or is denied. So the reveal is an explicit
 * action, the result is never cached, and closing the panel drops it.
 *
 * The retained snapshot expires. When the server answers 410 the panel says
 * the snapshot was purged rather than showing an error — a purge is the
 * retention policy working, not a fault.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, ShieldAlert } from "lucide-react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { ApiError } from "@/lib/api/errors";
import { formatDateTime, formatQuantity } from "@/lib/format";
import { useChannelNames } from "@/features/channels/queries";

import { AllocatePanel } from "../components/allocate-panel";

import type { AddressSnapshot, SalesMoney } from "../api";
import { useSalesOrder, useSalesOrderPii } from "../queries";
import { SalesOrderStatusBadge } from "../components/status-badge";

/** Wire quantities are NUMERIC(20,6) strings; six is their own scale. */
const QTY_SCALE = 6;

export interface OrderDetailPageProps {
  salesOrderId: string;
  /** `sales.order.pii.read` — without it the reveal control is not rendered. */
  canReadPii?: boolean;
  canReadChannels?: boolean;
  /** `sales.fulfilment.allocate` — without it no promise can be made here. */
  canAllocate?: boolean;
  onBack: () => void;
  onOpenFulfilment?: (fulfilmentId: string) => void;
}

function AddressBlock({ title, address }: { title: string; address: AddressSnapshot | undefined }) {
  if (address === undefined) return null;
  const parts = [
    address.recipient_name,
    address.company,
    address.street_line1,
    address.street_line2,
    [address.postal_code, address.city].filter(Boolean).join(" "),
    address.region,
    address.country_code,
    address.phone,
  ].filter((part) => part !== undefined && part !== "");
  return (
    <div className="space-y-1">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h3>
      <address className="text-sm not-italic leading-relaxed">
        {parts.map((part) => (
          <span key={part} className="block">
            {part}
          </span>
        ))}
      </address>
    </div>
  );
}

export function OrderDetailPage({
  salesOrderId,
  canReadPii = false,
  canReadChannels = true,
  canAllocate = false,
  onBack,
  onOpenFulfilment,
}: OrderDetailPageProps) {
  const { t, i18n } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const detail = useSalesOrder(salesOrderId);
  const pii = useSalesOrderPii(salesOrderId, canReadPii && revealed);
  const { channelName } = useChannelNames(canReadChannels);

  const when = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };
  const qty = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, QTY_SCALE);
    } catch {
      return value;
    }
  };

  if (detail.isLoading) {
    return <p className="p-4 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>;
  }
  if (detail.isError || detail.data === undefined) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("sales.orderDetail.loadError", "This order could not be loaded.")}
        </p>
        <Button type="button" variant="outline" onClick={() => void detail.refetch()}>
          {t("actions.retry")}
        </Button>
      </div>
    );
  }

  const { order, lines } = detail.data;
  const totals: { key: string; label: string; value: SalesMoney }[] = [
    { key: "subtotal", label: t("sales.orderDetail.subtotal", "Subtotal"), value: order.subtotal },
    {
      key: "discount",
      label: t("sales.orderDetail.discount", "Discount"),
      value: order.discount_total,
    },
    { key: "tax", label: t("sales.orderDetail.tax", "Tax"), value: order.tax_total },
    {
      key: "shipping",
      label: t("sales.orderDetail.shipping", "Shipping"),
      value: order.shipping_total,
    },
  ];
  const piiPurged = pii.error instanceof ApiError && pii.error.status === 410;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold tracking-tight">
            <span dir="ltr" className="font-mono">
              {order.order_number ?? order.external_ref ?? order.id}
            </span>
            <SalesOrderStatusBadge status={order.status} />
          </h1>
          <p className="text-sm text-muted-foreground">
            {order.channel_id === undefined
              ? t("sales.orders.internal", "Internal")
              : channelName(order.channel_id)}
            {" · "}
            {when(order.placed_at ?? order.created_at)}
            {order.source_revision === undefined
              ? null
              : ` · ${t("sales.orderDetail.revision", "Source revision {{n}}", {
                  n: order.source_revision,
                })}`}
          </p>
        </div>
        <Button type="button" variant="ghost" onClick={onBack}>
          {t("sales.orderDetail.backToList", "Back to orders")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("sales.orderDetail.customerTitle", "Customer")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!canReadPii ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "sales.orderDetail.piiForbidden",
                "Customer details need the sales.order.pii.read grant.",
              )}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant={revealed ? "outline" : "default"}
                  onClick={() => setRevealed(!revealed)}
                >
                  {revealed ? (
                    <EyeOff className="size-4" aria-hidden="true" />
                  ) : (
                    <Eye className="size-4" aria-hidden="true" />
                  )}
                  {revealed
                    ? t("sales.orderDetail.hidePii", "Hide customer details")
                    : t("sales.orderDetail.revealPii", "Show customer details")}
                </Button>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ShieldAlert className="size-3.5" aria-hidden="true" />
                  {t("sales.orderDetail.piiAudited", "Every view of these details is recorded.")}
                </span>
              </div>

              {revealed && pii.isLoading ? (
                <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
              ) : null}
              {revealed && piiPurged ? (
                <p role="status" className="text-sm text-muted-foreground">
                  {t(
                    "sales.orderDetail.piiPurged",
                    "These details were deleted when their retention period ended.",
                  )}
                </p>
              ) : null}
              {revealed && pii.isError && !piiPurged ? (
                <p role="alert" className="text-sm text-destructive">
                  {t("sales.orderDetail.piiError", "Customer details could not be read.")}
                </p>
              ) : null}
              {revealed && pii.data !== undefined ? (
                <div className="space-y-4">
                  <div className="space-y-1 text-sm">
                    {pii.data.customer_name === undefined ? null : (
                      <p className="font-medium">{pii.data.customer_name}</p>
                    )}
                    {pii.data.customer_email === undefined ? null : (
                      <p dir="ltr" className="text-muted-foreground">
                        {pii.data.customer_email}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <AddressBlock
                      title={t("sales.orderDetail.shippingAddress", "Shipping address")}
                      address={pii.data.shipping_address}
                    />
                    <AddressBlock
                      title={t("sales.orderDetail.billingAddress", "Billing address")}
                      address={pii.data.billing_address}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "sales.orderDetail.piiRetention",
                      "Kept until {{when}} (policy {{policy}}).",
                      {
                        when: when(pii.data.pii_retention_expires_at),
                        policy: pii.data.pii_policy_version,
                      },
                    )}
                  </p>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      {canAllocate ? (
        <AllocatePanel salesOrderId={salesOrderId} lines={lines} onAllocated={onOpenFulfilment} />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("sales.orderDetail.linesTitle", "Order lines")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th scope="col" className="p-2 text-start">
                  #
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("sales.orderDetail.columns.sku", "SKU")}
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("sales.orderDetail.columns.description", "Description")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("sales.orderDetail.columns.qty", "Qty")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("sales.orderDetail.columns.unitPrice", "Unit price")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("sales.orderDetail.columns.tax", "Tax")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("sales.orderDetail.columns.lineTotal", "Line total")}
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id} className="border-b last:border-0">
                  <td className="p-2 text-muted-foreground">{line.line_number}</td>
                  <td className="p-2">
                    <span dir="ltr" className="font-mono text-xs">
                      {line.sku}
                    </span>
                    {line.source_product_type === undefined ? null : (
                      <Badge variant="outline" className="ms-2 align-middle text-[10px]">
                        {t(
                          `sales.productType.${line.source_product_type}`,
                          line.source_product_type,
                        )}
                      </Badge>
                    )}
                  </td>
                  <td className="p-2">{line.description}</td>
                  <td className="p-2 text-end tabular-nums">{qty(line.qty_ordered)}</td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.unit_price} display="none" />
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.tax_amount} display="none" />
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.line_total} display="none" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="sm:max-w-md sm:ms-auto">
        <CardHeader>
          <CardTitle>
            <h2>{t("sales.orderDetail.totalsTitle", "Totals")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {totals.map((row) => (
            <div key={row.key} className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">{row.label}</span>
              <MoneyDisplay value={row.value} />
            </div>
          ))}
          <div className="flex items-center justify-between gap-6 border-t pt-2 font-semibold">
            <span>{t("sales.orderDetail.grandTotal", "Grand total")}</span>
            <MoneyDisplay value={order.grand_total} />
          </div>
          {order.base_grand_total === undefined || order.base_to_order_rate === undefined ? null : (
            <div className="flex items-center justify-between gap-6 text-xs text-muted-foreground">
              <span>
                {t("sales.orderDetail.baseTotal", "In base currency (rate {{rate}})", {
                  rate: order.base_to_order_rate,
                })}
              </span>
              <MoneyDisplay value={order.base_grand_total} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
